import { useEffect, useState, useMemo } from 'react';
import readXlsxFile, { Row } from 'read-excel-file';
import {
  useActivitySessionDownloadSubmitters,
  useActivitySessionFindOne,
  useActivityV3FindOne,
} from 'src/generated/endpoint';

type StudentRow = Row;

// 학생 정보 학년, 반, 번호순 정렬 함수
const sortStudentsByGradeClassNumber = (students: StudentRow[]): StudentRow[] => {
  return students.sort((current, next) => {
    const currentGrade = Number(current[0]) || 0;
    const nextGrade = Number(next[0]) || 0;

    const currentClass = Number(current[1]) || 0;
    const nextClass = Number(next[1]) || 0;

    const currentNumber = Number(current[2]) || 0;
    const nextNumber = Number(next[2]) || 0;

    if (currentGrade !== nextGrade) {
      return currentGrade - nextGrade;
    }

    if (currentClass !== nextClass) {
      return currentClass - nextClass;
    }

    return currentNumber - nextNumber;
  });
};

// 제출 시간 포맷팅 함수
const formatSubmittedTime = (students: StudentRow[]): StudentRow[] => {
  return students.map((row) => {
    const newRow = [...row];
    newRow[5] = row[5] ? row[5].toString().slice(0, 16) : '';
    return newRow;
  });
};

// 미제출자 필터링 함수
const filterBySubmissionStatus = (students: StudentRow[], showOnlyNotSubmitted: boolean): StudentRow[] => {
  if (showOnlyNotSubmitted) {
    return students.filter((row) => row[4] === '미제출');
  }
  return students;
};

export const useSessionDownloadData = (sessionId: number | undefined, userId: number | undefined) => {
  const [checked, setChecked] = useState(false);
  const [rows, setRows] = useState<Row[]>();

  // API 호출
  const { data: activitySession } = useActivitySessionFindOne(Number(sessionId), {
    query: { enabled: !!sessionId },
  });

  const { data: activityv3, isLoading: isGetActivityLoading } = useActivityV3FindOne(
    Number(activitySession?.activityv3Id),
    undefined,
    {
      query: { enabled: !!activitySession?.activityv3Id },
    },
  );

  // 그룹 ID 계산
  const groupIds = useMemo(() => {
    if (!activityv3 || !userId) return [];

    if (activityv3.isJointActivity) {
      return activityv3.teacherActivityV3s?.find((tav3) => tav3.userId === userId)?.groupIds || [];
    }

    return activityv3.groupActivityV3s?.map((el) => el.groupId) || [];
  }, [activityv3, userId]);

  const { data: excelData, isLoading: isActivityDownloadLoading } = useActivitySessionDownloadSubmitters(
    Number(sessionId),
    { groupIds },
  );

  // 엑셀 데이터 처리
  useEffect(() => {
    if (!excelData) return;

    const processExcelData = async () => {
      try {
        const rows = await readXlsxFile(excelData);
        setRows(rows);
      } catch (error) {
        console.error('엑셀 파일 읽기 실패:', error);
      }
    };

    processExcelData().catch(console.error);
  }, [sessionId, excelData]);

  // 테이블 데이터 처리
  const { tableHeader, processedTableBody } = useMemo(() => {
    if (!rows || rows.length === 0) {
      return { tableHeader: undefined, processedTableBody: undefined };
    }

    const [header, ...body] = rows;

    // 데이터 처리 파이프라인
    const sortedBody = sortStudentsByGradeClassNumber(body);
    const formattedBody = formatSubmittedTime(sortedBody);

    return {
      tableHeader: header,
      processedTableBody: formattedBody,
    };
  }, [rows]);

  // 필터링된 테이블 바디
  const filteredTableBody = useMemo(() => {
    if (!processedTableBody) return undefined;

    return filterBySubmissionStatus(processedTableBody, checked);
  }, [processedTableBody, checked]);

  const isLoading = isActivityDownloadLoading || isGetActivityLoading;

  return {
    checked,
    setChecked,
    tableHeader,
    filteredTableBody,
    activitySession,
    isLoading,
  };
};
